<template>
  <div>
    <AppNavbar />
    <div class="cookie-pref-page">
      <section class="hero-outer-bleed">
        <div class="hero-section">
          <div class="hero-glow one"></div>
          <div class="hero-glow two"></div>

          <div class="hero-container reveal">
            <h1>Cookie Preferences</h1>
          </div>
        </div>
      </section>

      <section class="content-outer-bleed">
        <div class="content-section">
          <div class="content-wrapper reveal">
            <p>
              IndieKonnect values your privacy and wants you to stay in control
              of your personal data. This Preference Centre lets you decide how
              we collect, store, and process your information on this website,
              in line with applicable Indian Data Protection Laws.
            </p>
            <p>
              Below, you can review each category of cookies and related data
              processing we carry out, and choose to enable or disable them as
              you wish. You're free to adjust these settings at any time, and
              you may withdraw your consent whenever you like — doing so won't
              affect any processing already carried out based on your earlier
              consent.
            </p>
            <p>
              For further details, please see our
              <router-link to="/privacy-policy">Privacy Policy</router-link>
            </p>

            <h2>Manage your preferences</h2>

            <div class="ik-toggle-row">
              <div class="ik-toggle-info">
                <h3>Essential Cookies</h3>
                <p>
                  Required for core site functionality like login, cart, and
                  checkout. Always active.
                </p>
              </div>
              <label class="ik-toggle ik-toggle-locked">
                <input type="checkbox" checked disabled />
                <span class="ik-toggle-slider"></span>
              </label>
            </div>

            <div class="ik-toggle-row">
              <div class="ik-toggle-info">
                <h3>Analytics &amp; Performance Cookies</h3>
                <p>
                  Help us understand how visitors use the site so we can improve
                  it.
                </p>
              </div>
              <label class="ik-toggle">
                <input type="checkbox" v-model="categories.analytics" />
                <span class="ik-toggle-slider"></span>
              </label>
            </div>

            <div class="ik-toggle-row">
              <div class="ik-toggle-info">
                <h3>Functionality Cookies</h3>
                <p>
                  Remember your preferences and choices to personalise your
                  experience.
                </p>
              </div>
              <label class="ik-toggle">
                <input type="checkbox" v-model="categories.functionality" />
                <span class="ik-toggle-slider"></span>
              </label>
            </div>

            <div class="ik-toggle-row">
              <div class="ik-toggle-info">
                <h3>Targeting &amp; Advertising Cookies</h3>
                <p>Used to show you more relevant content and offers.</p>
              </div>
              <label class="ik-toggle">
                <input type="checkbox" v-model="categories.targeting" />
                <span class="ik-toggle-slider"></span>
              </label>
            </div>

            <div class="ik-policy-actions">
              <button class="ik-btn ik-btn-accept" @click="handleSave">
                Save Preferences
              </button>
              <span v-if="justSaved" class="ik-saved-note"
                >Preferences saved.</span
              >
            </div>
          </div>
        </div>
      </section>
    </div>
    <FooterView />
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, watch } from "vue";
import { useCookieConsentStore } from "@/stores/useCookieConsentStore";
import FooterView from "../FooterView.vue";
import AppNavbar from "../AppNavbar.vue";

const store = useCookieConsentStore();
const justSaved = ref(false);
const categories = reactive({ ...store.categories });

watch(
  () => store.categories,
  (nextCategories) => {
    Object.assign(categories, nextCategories || {});
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  store.init();
  Object.assign(categories, store.categories);

  const body = document.body;
  if (body) {
    body.style.setProperty("margin", "0", "important");
    body.style.setProperty("padding", "0", "important");
    body.style.setProperty("max-width", "100%", "important");
  }

  const revealElements = document.querySelectorAll(".reveal");
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    },
    { threshold: 0.08 },
  );
  revealElements.forEach((el) => observer.observe(el));
});

function handleSave() {
  store.setCategory("analytics", categories.analytics);
  store.setCategory("functionality", categories.functionality);
  store.setCategory("targeting", categories.targeting);
  store.savePreferences();
  justSaved.value = true;
  setTimeout(() => (justSaved.value = false), 2500);
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800&display=swap");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.cookie-pref-page {
  width: 100% !important;
  max-width: 100% !important;
  font-family: "Raleway", sans-serif;
  background: #ffffff;
  color: #2d3748;
}

.hero-outer-bleed {
  width: 100% !important;
  background: #003da5 !important;
  display: block;
}

.hero-section {
  position: relative;
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 50px 24px 20px;
  overflow: hidden;
  z-index: 1;
}

.hero-container {
  width: 100%;
  position: relative;
  z-index: 2;
}

.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
}

.hero-glow.one {
  width: 350px;
  height: 350px;
  background: rgba(255, 199, 44, 0.15);
  top: -100px;
  right: -50px;
}

.hero-glow.two {
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.06);
  bottom: -100px;
  left: -50px;
}

.hero-container h1 {
  color: #ffc72c;
  font-size: 46px;
  line-height: 1.1;
  margin-bottom: 16px;
  font-weight: 800;
}

.content-outer-bleed {
  width: 100% !important;
  background: #ffffff;
  display: block;
}

.content-section {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 35px 24px 60px;
}

.content-wrapper {
  width: 100%;
}

.content-wrapper h2 {
  font-size: 28px;
  line-height: 1.3;
  color: #003da5;
  margin: 32px 0 16px;
  font-weight: 800;
}

.content-wrapper p {
  color: #4c5661;
  font-size: 16px;
  line-height: 1.9;
  font-weight: 500;
  max-width: 980px;
  margin-bottom: 12px;
}

.content-wrapper p a {
  color: #003da5;
  text-decoration: underline;
  font-weight: 600;
}

.content-wrapper p a:hover {
  color: #ffc72c;
}

.reveal {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.ik-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #eee;
  max-width: 620px; /* NEW — pulls the toggle in near the text */
}

.ik-toggle-info h3 {
  margin: 0 0 4px;
  font-size: 15px;
  color: #1a1a1a;
}

.ik-toggle-info p {
  margin: 0;
  font-size: 13px;
  color: #666;
}

.ik-toggle {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
}

.ik-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.ik-toggle-slider {
  position: absolute;
  inset: 0;
  background: #ccc;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.ik-toggle-slider::before {
  content: "";
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.ik-toggle input:checked + .ik-toggle-slider {
  background: #003da5;
}

.ik-toggle input:checked + .ik-toggle-slider::before {
  transform: translateX(20px);
}

.ik-toggle-locked .ik-toggle-slider {
  background: #ffc72c;
  cursor: not-allowed;
}

.ik-policy-actions {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.ik-btn-accept {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  padding: 0 36px;
  background: linear-gradient(135deg, var(--gold) 0%, #f5b000 100%);
  color: var(--dark-blue);
  border: none;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s;
  font-family: inherit;
  box-shadow: 0 8px 20px rgba(255, 199, 44, 0.2);
}

.ik-btn-accept:hover {
  background: #003da5;
  border-color: #003da5;
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 61, 165, 0.3);
  transform: translateY(-1px);
}

.ik-saved-note {
  color: #2c8a3d;
  font-size: 13px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .hero-section {
    padding: 40px 20px 20px;
  }

  .hero-container h1 {
    font-size: 26px;
  }

  .content-section {
    padding: 30px 20px 40px;
  }

  .content-wrapper p {
    font-size: 15px;
    line-height: 1.8;
  }
}
</style>
